// Custom i18n — replaces i18next/react-i18next
// Supports: namespaces, dot-path lookup, {{interpolation}}, _one/_other pluralization,
// language detection (localStorage → navigator), lazy JSON loading via React Suspense.

const cache = new Map<string, Record<string, unknown>>();
const pending = new Map<string, Promise<void>>();

const SUPPORTED_LANGS = ["en", "es", "fr", "de", "it", "ja", "pt"];
const STORAGE_KEY = "prezntz_language";

function detectLang(): string {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && SUPPORTED_LANGS.includes(stored)) return stored;
  } catch { /* SSR / test env */ }
  try {
    const nav = navigator.language.split("-")[0];
    if (SUPPORTED_LANGS.includes(nav)) return nav;
  } catch { /* test env */ }
  return "en";
}

let currentLang = detectLang();

async function loadNs(lang: string, ns: string): Promise<void> {
  const key = `${lang}:${ns}`;
  if (cache.has(key)) return;
  try {
    const mod = await import(`../locales/${lang}/${ns}.json`);
    cache.set(key, mod.default as Record<string, unknown>);
  } catch {
    if (lang !== "en") {
      try {
        const mod = await import(`../locales/en/${ns}.json`);
        cache.set(key, mod.default as Record<string, unknown>);
      } catch { /* no translation available */ }
    }
  }
}

function get(lang: string, ns: string, key: string): string | undefined {
  const translations = cache.get(`${lang}:${ns}`);
  if (!translations) return undefined;
  const val = key.split(".").reduce<unknown>(
    (obj, k) => (obj && typeof obj === "object" ? (obj as Record<string, unknown>)[k] : undefined),
    translations
  );
  return typeof val === "string" ? val : undefined;
}

function interpolate(str: string, vars: Record<string, unknown>): string {
  return str.replace(/\{\{(\w+)\}\}/g, (_, k) => String(vars[k] ?? ""));
}

export function useTranslation(ns = "common") {
  const key = `${currentLang}:${ns}`;

  // Suspend while translations are loading (React Suspense)
  if (!cache.has(key)) {
    if (!pending.has(key)) pending.set(key, loadNs(currentLang, ns));
    throw pending.get(key);
  }

  function t(transKey: string, options?: Record<string, unknown>): string {
    const count = options?.count as number | undefined;
    const resolvedKey =
      count !== undefined
        ? count === 0
          ? // Try _zero first, then fall back to _other
            get(currentLang, ns, `${transKey}_zero`) !== undefined ||
            get("en", ns, `${transKey}_zero`) !== undefined
              ? `${transKey}_zero`
              : `${transKey}_other`
          : count === 1
            ? `${transKey}_one`
            : `${transKey}_other`
        : transKey;

    const raw =
      get(currentLang, ns, resolvedKey) ??
      get("en", ns, resolvedKey) ??
      transKey;

    return options ? interpolate(raw, options) : raw;
  }

  const i18n = {
    language: currentLang,
    changeLanguage: (lang: string) => {
      if (!SUPPORTED_LANGS.includes(lang)) return;
      currentLang = lang;
      try { localStorage.setItem(STORAGE_KEY, lang); } catch { /* ignore */ }
      const k = `${lang}:${ns}`;
      if (!pending.has(k)) pending.set(k, loadNs(lang, ns));
    },
  };

  return { t, i18n };
}

// Eagerly start loading the common namespace for the detected language
// so translations are likely ready before React's first render.
{
  const k = `${currentLang}:common`;
  if (!pending.has(k)) pending.set(k, loadNs(currentLang, "common"));
}
